<template>
  <div>
    <h5 class="etapa-titulo">Quais produtos entram nesta grade?</h5>
    <p class="step-description">
      O produto entra quando <strong>todas</strong> as condições forem verdadeiras.
    </p>

    <div v-if="!regras.length" class="estado-vazio text-center mb-3">
      <div class="scope-icon mx-auto mb-2">
        <fa-icon icon="filter" />
      </div>
      <strong class="d-block">Nenhuma condição ainda</strong>
      <small class="d-block text-muted mb-3">
        Comece pelo atributo mais largo da categoria: marca, tipo ou dimensão.
      </small>
      <CButton color="primary" @click="adicionar">
        <CIcon name="cil-plus" size="sm" class="mr-1" />
        Adicionar a primeira condição
      </CButton>
    </div>

    <template v-else>
      <div
        v-for="(regra, indice) in regras"
        :key="`regra-${indice}`"
        class="condicao mb-2"
      >
        <CBadge :color="indice === 0 ? 'primary' : 'info'" class="condicao__conector">
          {{ indice === 0 ? "SE" : "E" }}
        </CBadge>

        <CRow class="align-items-end">
          <CCol col="12" md="5">
            <label class="condicao__label">Atributo do produto</label>
            <multiselect
              :value="opcaoColuna(regra)"
              :options="colunasFiltradas(regra.colunaProduto)"
              :searchable="true"
              :show-labels="false"
              :allow-empty="false"
              label="label"
              track-by="coluna"
              placeholder="Escolha o atributo..."
              @select="onColunaChange(indice, $event)"
            />
          </CCol>

          <CCol col="12" md="6">
            <label class="condicao__label">
              <span class="d-none d-md-inline text-lowercase">é igual a </span>Valor
            </label>

            <DropdownInput
              v-if="regra.colunaProduto === 'id'"
              :value="regra.valor"
              label=""
              @input="onProdutoChange(indice, $event)"
            />

            <multiselect
              v-else-if="regra.colunaProduto && temValoresPredef(regra.colunaProduto)"
              :value="opcaoValor(regra)"
              :options="valoresPorColuna[regra.colunaProduto] || []"
              :searchable="true"
              :show-labels="false"
              :allow-empty="false"
              :loading="!!loadingValores[regra.colunaProduto]"
              label="label"
              track-by="id"
              :placeholder="
                loadingValores[regra.colunaProduto] ? 'Carregando...' : 'Escolha um valor...'
              "
              @select="onValorChange(indice, $event)"
            >
              <template #option="{ option }">
                <span>{{ option.label }}</span>
                <CBadge
                  v-if="precisaDesambiguar(regra.colunaProduto, option)"
                  color="light"
                  class="ml-1"
                  title="Código do valor no ERP"
                >
                  {{ option.id }}
                </CBadge>
              </template>
              <template #singleLabel="{ option }">
                {{ option.label }}
                <CBadge
                  v-if="precisaDesambiguar(regra.colunaProduto, option)"
                  color="light"
                  class="ml-1"
                  title="Código do valor no ERP"
                >
                  {{ option.id }}
                </CBadge>
              </template>
            </multiselect>

            <input
              v-else
              type="text"
              class="form-control"
              :placeholder="
                regra.colunaProduto ? 'Informe o valor exato' : 'Escolha o atributo primeiro'
              "
              :disabled="!regra.colunaProduto"
              v-model="regra.valor"
              maxlength="100"
            />
          </CCol>

          <CCol col="12" md="1" class="text-md-right mt-2 mt-md-0">
            <CButton
              size="sm"
              color="danger"
              variant="outline"
              title="Remover condição"
              @click="remover(indice)"
            >
              <fa-icon icon="trash-alt" />
            </CButton>
          </CCol>
        </CRow>
      </div>

      <CButton color="primary" variant="outline" size="sm" class="mb-3" @click="adicionar">
        <CIcon name="cil-plus" size="sm" class="mr-1" />
        Adicionar condição
      </CButton>
    </template>

    <!-- ===== ALCANCE (diagnóstico ao vivo) ===== -->
    <CCard class="mb-0">
      <CCardHeader class="d-flex justify-content-between align-items-center">
        <strong>Alcance destas condições</strong>
        <small v-if="carregando" class="text-muted">
          <CSpinner size="sm" class="mr-1" />
          calculando...
        </small>
      </CCardHeader>

      <CCardBody>
        <p v-if="!temCondicoesValidas" class="text-muted mb-0">
          Complete uma condição (atributo + valor) para eu contar quantos produtos entram
          e quantas grades se formariam.
        </p>

        <!-- Falha na contagem: sem isto o "Contando..." fica eterno, porque
             diagnóstico nulo é o mesmo estado de cálculo em andamento. -->
        <CAlert v-else-if="erro" color="warning" class="mb-0">
          <div class="d-flex align-items-start justify-content-between gap-2">
            <div>
              <strong>Não foi possível contar os produtos</strong>
              <p class="mb-0 small">{{ erro }}</p>
              <p class="mb-0 small text-muted">
                As condições continuam válidas e o grupo pode ser salvo — só o alcance
                deixou de ser exibido.
              </p>
            </div>
            <CButton size="sm" color="warning" variant="outline" @click="$emit('recalcular')">
              <fa-icon icon="sync-alt" class="mr-1" />
              Tentar de novo
            </CButton>
          </div>
        </CAlert>

        <p v-else-if="!temDiagnostico" class="text-muted mb-0">
          Contando os produtos que casam com estas condições...
        </p>

        <template v-else>
          <CRow>
            <CCol col="12" md="4" class="mb-3 mb-md-0">
              <div class="indicador">
                <span class="indicador__valor">{{ diagnostico.total_produtos }}</span>
                <span class="indicador__rotulo">produtos entram</span>
              </div>
            </CCol>
            <CCol col="12" md="4" class="mb-3 mb-md-0">
              <div class="indicador">
                <span class="indicador__valor">{{ diagnostico.total_grades }}</span>
                <span class="indicador__rotulo">grades se formam</span>
                <small class="text-muted">uma por agrupador diferente</small>
              </div>
            </CCol>
            <CCol col="12" md="4">
              <div class="indicador">
                <span
                  class="indicador__valor"
                  :class="{ 'text-danger': excedeLimite }"
                >
                  {{ diagnostico.maior_grade }}
                </span>
                <span class="indicador__rotulo">SKUs na maior grade</span>
                <CProgress
                  size="xs"
                  class="my-1"
                  :color="excedeLimite ? 'danger' : 'info'"
                  :value="diagnostico.maior_grade"
                  :max="diagnostico.limite_permitido"
                />
                <small class="text-muted">limite de {{ diagnostico.limite_permitido }}</small>
              </div>
            </CCol>
          </CRow>

          <CAlert v-if="excedeLimite" color="danger" class="mt-3 mb-0">
            <fa-icon icon="exclamation-triangle" class="mr-1" />
            <strong>Acima do limite de {{ diagnostico.limite_permitido }} SKUs por grade.</strong>
            Acrescente uma condição mais específica para quebrar em grades menores.
          </CAlert>

          <CAlert v-if="diagnostico.total_sem_agrupador" color="warning" class="mt-3 mb-0">
            <fa-icon icon="exclamation-triangle" class="mr-1" />
            <strong>
              {{ diagnostico.total_sem_agrupador }} de {{ diagnostico.total_produtos }} ficarão
              sem grade
            </strong>
            — estão sem agrupador de grade, que vem da carga do ERP. Não impede salvar.
            <CButton
              v-if="diagnostico.exemplos_sem_agrupador.length"
              size="sm"
              color="link"
              class="p-0 align-baseline"
              @click="exemplosAbertos = !exemplosAbertos"
            >
              {{ exemplosAbertos ? "ocultar exemplos" : "ver exemplos" }}
            </CButton>
            <CCollapse :show="exemplosAbertos">
              <ul class="mb-0 mt-2 pl-0 list-unstyled">
                <li v-for="produto in diagnostico.exemplos_sem_agrupador" :key="produto.id">
                  <CBadge color="light" class="mr-1">{{ produto.id }}</CBadge>
                  {{ produto.descComp }}
                </li>
              </ul>
            </CCollapse>
          </CAlert>

          <CAlert
            v-if="!diagnostico.total_produtos && !carregando"
            color="info"
            class="mt-3 mb-0"
          >
            <fa-icon icon="info-circle" class="mr-1" />
            <strong>Nenhum produto casa com essas condições hoje.</strong>
            Revise o valor — ou salve assim, se os produtos vêm na próxima carga.
          </CAlert>
        </template>
      </CCardBody>
    </CCard>
  </div>
</template>

<script>
import { get } from "@/helpers/apiRequest";
import Multiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.min.css";
import DropdownInput from "@/views/Campanhas/components/CampanhasComerciais/components/Regra/DropdownInput.vue";

export default {
  name: "EtapaProdutos",
  components: { Multiselect, DropdownInput },
  props: {
    form: { type: Object, required: true },
    colunasDisponiveis: { type: Array, default: () => [] },
    /** Resultado de POST /admin/grupo-grade/diagnostico, calculado pelo pai. */
    diagnostico: { type: Object, default: null },
    carregando: { type: Boolean, default: false },
    /** Mensagem de falha do diagnóstico; nulo quando não houve erro. */
    erro: { type: String, default: null },
  },
  data() {
    return {
      valoresPorColuna: {},
      loadingValores: {},
      exemplosAbertos: false,
    };
  },
  computed: {
    regras() {
      if (!this.form.regras) {
        this.$set(this.form, "regras", []);
      }
      return this.form.regras;
    },
    colunasMap() {
      return this.colunasDisponiveis.reduce((acumulador, opcao) => {
        acumulador[opcao.coluna] = opcao;
        return acumulador;
      }, {});
    },
    temCondicoesValidas() {
      return this.regras.some(
        (regra) => regra.colunaProduto && String(regra.valor || "").trim()
      );
    },
    temDiagnostico() {
      return !!this.diagnostico;
    },
    excedeLimite() {
      return !!(this.diagnostico && this.diagnostico.excede_limite);
    },
    /**
     * Descrições repetidas dentro de cada coluna. O código do valor (id da
     * dimensão/marca no ERP) só aparece nesses casos: fora deles ele não informa
     * nada a quem cadastra e só suja a lista.
     *
     * @returns {Object} { coluna: Set<label> }
     */
    rotulosAmbiguos() {
      return Object.keys(this.valoresPorColuna).reduce((acumulador, coluna) => {
        const contagem = {};
        (this.valoresPorColuna[coluna] || []).forEach((valor) => {
          const rotulo = String(valor.label);
          contagem[rotulo] = (contagem[rotulo] || 0) + 1;
        });

        acumulador[coluna] = new Set(
          Object.keys(contagem).filter((rotulo) => contagem[rotulo] > 1)
        );
        return acumulador;
      }, {});
    },
  },
  watch: {
    regras: {
      immediate: true,
      handler(novasRegras) {
        (novasRegras || []).forEach((regra) => {
          if (regra.colunaProduto && this.temValoresPredef(regra.colunaProduto)) {
            this.garantirValoresCarregados(regra.colunaProduto);
          }
        });
      },
    },
  },
  methods: {
    adicionar() {
      this.regras.push({ colunaProduto: null, valor: "" });
    },
    remover(indice) {
      this.regras.splice(indice, 1);
    },
    colunasFiltradas(colunaAtual) {
      const colunasUsadas = this.regras
        .map((regra) => regra.colunaProduto)
        .filter((coluna) => coluna && coluna !== colunaAtual);
      return this.colunasDisponiveis.filter((opcao) => !colunasUsadas.includes(opcao.coluna));
    },
    opcaoColuna(regra) {
      if (!regra.colunaProduto) return null;
      return this.colunasMap[regra.colunaProduto] || null;
    },
    opcaoValor(regra) {
      if (!regra.colunaProduto || !regra.valor) return null;
      const valoresDisponiveis = this.valoresPorColuna[regra.colunaProduto] || [];
      return (
        valoresDisponiveis.find((valor) => String(valor.id) === String(regra.valor)) || null
      );
    },
    onColunaChange(indice, opcaoSelecionada) {
      const novaColuna = opcaoSelecionada ? opcaoSelecionada.coluna : null;
      this.$set(this.regras[indice], "colunaProduto", novaColuna);
      this.$set(this.regras[indice], "valor", "");
      if (novaColuna && this.temValoresPredef(novaColuna)) {
        this.garantirValoresCarregados(novaColuna);
      }
    },
    onValorChange(indice, opcaoSelecionada) {
      this.$set(
        this.regras[indice],
        "valor",
        opcaoSelecionada ? String(opcaoSelecionada.id) : ""
      );
    },
    onProdutoChange(indice, produtoId) {
      this.$set(this.regras[indice], "valor", produtoId ? String(produtoId) : "");
    },
    precisaDesambiguar(coluna, opcao) {
      const ambiguos = this.rotulosAmbiguos[coluna];
      return !!(ambiguos && ambiguos.has(String(opcao.label)));
    },
    temValoresPredef(coluna) {
      const opcaoColuna = this.colunasMap[coluna];
      return !!(opcaoColuna && opcaoColuna.tem_valores_predef);
    },
    garantirValoresCarregados(coluna) {
      if (this.valoresPorColuna[coluna] || this.loadingValores[coluna]) return;
      this.$set(this.loadingValores, coluna, true);
      get(`/admin/grupo-grade/valores-coluna?coluna=${encodeURIComponent(coluna)}`)
        .then((json) => {
          this.$set(this.valoresPorColuna, coluna, json.data || json || []);
        })
        .catch(() => {
          this.$set(this.valoresPorColuna, coluna, []);
        })
        .finally(() => {
          this.$set(this.loadingValores, coluna, false);
        });
    },
  },
};
</script>

<style scoped>
.etapa-titulo {
  font-weight: 600;
  color: #1d2939;
}

.step-description {
  color: #667085;
  font-size: 13px;
  margin-bottom: 20px;
  max-width: 72ch;
}

.estado-vazio {
  padding: 32px 24px;
  border: 1px dashed #dddce9;
  border-radius: 10px;
  background: #f9fafb;
}

.scope-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f2f2f8;
  color: #3a3e81;
}

/* ===== CONDIÇÕES ===== */
.condicao {
  position: relative;
  padding: 14px 16px 14px 52px;
  border: 1px solid #e4e7eb;
  border-radius: 10px;
}

.condicao__conector {
  position: absolute;
  top: 16px;
  left: 12px;
  min-width: 30px;
}

.condicao__label {
  font-size: 11px;
  font-weight: 600;
  color: #667085;
  margin-bottom: 4px;
}

/* ===== INDICADORES ===== */
.indicador {
  display: flex;
  flex-direction: column;
  padding: 12px 14px;
  border-radius: 8px;
  background: #f9fafb;
}

.indicador__valor {
  font-size: 24px;
  font-weight: 700;
  line-height: 1.1;
}

.indicador__rotulo {
  font-size: 12px;
  color: #667085;
}

/* Multiselect e o autocomplete de produto vêm de libs externas: o ajuste fino
   precisa atravessar o escopo, mas continua preso a este componente. */
.condicao ::v-deep .multiselect,
.condicao ::v-deep .multiselect__tags {
  min-height: 35px;
  font-size: 13px;
}

.condicao ::v-deep .multiselect__tags {
  padding: 7px 36px 0 10px;
  border: 1px solid #dddce9;
}

.condicao ::v-deep .multiselect__single,
.condicao ::v-deep .multiselect__placeholder {
  font-size: 13px;
  margin-bottom: 6px;
  padding-top: 1px;
}

.condicao ::v-deep .multiselect--active .multiselect__tags {
  border-color: #3a3e81;
}

.condicao ::v-deep .multiselect__option--highlight,
.condicao ::v-deep .multiselect__option--highlight::after {
  background: #3a3e81;
}

.condicao ::v-deep .multiselect__option--selected.multiselect__option--highlight {
  background: #2298a3;
}

.condicao ::v-deep .multiselect__content-wrapper {
  border: 1px solid #dddce9;
  z-index: 5;
}

.condicao ::v-deep .regra-produto {
  margin-right: 0;
}

.condicao ::v-deep .regra-produto > label {
  display: none;
}
</style>
